import { observer } from 'mobx-react-lite';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import {
  hasSwatchImages,
  VariantSwatchGrid,
} from '~/components/VariantSwatchGrid';
import type { ProductPropertiesValue } from '~/types/Product/interface';

import styles from './ProductPropertyFilter.module.scss';

const TitleComponent = company.match({
  mcosmetic: Typography.Text200Medium,
  default: Typography.Text100Medium,
});

type Props = {
  filter: ProductPropertiesValue;
  handleChangeOffer: (sku: string) => () => void;
  name?: string;
  withTitle?: boolean;
  classes?: {
    filterListContainerClassName?: string;
    filterListClassName?: string;
    filterItemClassName?: string;
    itemStateClassNames?: {
      passed?: string;
      empty?: string;
      colorList?: string;
    };
  };
};

const ProductPropertyFilter = ({
  filter,
  handleChangeOffer,
  name,
  withTitle = true,
  classes,
}: Props) => {
  const selectedChoice = filter.choices.find((i) => i.selected);
  const inputName = `${filter.code}_${name ?? ''}`;

  const {
    filterListContainerClassName,
    filterListClassName,
    filterItemClassName,
    itemStateClassNames,
  } = classes || {};

  const {
    passed = styles.passed,
    empty = styles.empty,
    colorList = styles.round,
  } = itemStateClassNames || {};

  const withImages = company.variantSwatchesEnabled && hasSwatchImages(filter);

  return (
    <div
      className={cn(styles.filterListContainer, filterListContainerClassName)}
    >
      {withTitle && Boolean(filter.name) && (
        <TitleComponent className={styles.filterListTitle}>
          {filter.name}:{' '}
          <span className={styles.filterListValue}>
            {selectedChoice?.label}
          </span>
        </TitleComponent>
      )}
      {withImages ? (
        <VariantSwatchGrid
          filter={filter}
          inputName={inputName}
          handleChangeOffer={handleChangeOffer}
        />
      ) : (
        <div className={cn(styles.filterList, filterListClassName)}>
          {filter.choices.map((choice) => {
            return (
              <label
                className={cn(
                  styles.filterListItem,
                  !choice.sellable && passed,
                  (!choice.sku || choice.combinationExists === false) && empty,
                  filter.type === 'colorList' && colorList,
                  filterItemClassName,
                )}
                style={
                  filter.type === 'colorList'
                    ? { backgroundColor: choice.value.toString() }
                    : {}
                }
                key={choice.value}
                data-choice-value={choice.value}
                data-type={filter.type}
              >
                <input
                  data-radio={choice.label}
                  type="radio"
                  name={inputName}
                  id={`${inputName}_${choice.value}`}
                  value={`${choice.value.toString()}`}
                  checked={Boolean(choice.selected)}
                  onChange={() => {
                    if (choice.sku) {
                      handleChangeOffer(choice.sku)();
                    }
                  }}
                />
                {filter.type !== 'colorList' && <span>{choice.label}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default observer(ProductPropertyFilter);
